import React, { useEffect, useState, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import { markPaymentForm } from '../config/logrocket';
import OverlaySpinner from './OverlaySpinner';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string; // now this is the lookup
  amount: number;
  onSuccess?: (paymentIntentId: string) => void;
}

const fetchClientSecret = async (apiBaseUrl: string, token: string, lookup: string) => {
  const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/${lookup}/payment-intent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ lookup }),
  });
  return res.json();
};

const PaymentForm: React.FC<{ clientSecret: string; onSuccess: (paymentIntentId: string) => void; onClose: () => void; amount: number; breakdown?: any; documentId: string; apiBaseUrl: string; }> = ({ clientSecret, onSuccess, onClose, amount, breakdown, documentId, apiBaseUrl }) => {
  const { getToken } = useKeycloak();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollTimeout, setPollTimeout] = useState(false);
  const payButtonRef = useRef<HTMLButtonElement>(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (payButtonRef.current) payButtonRef.current.focus();
  }, []);

  // Mark payment form as sensitive for LogRocket exclusion
  useEffect(() => {
    if (formRef.current) {
      markPaymentForm(formRef.current);
    }
  }, []);

  // Poll payment status from backend
  const pollPaymentStatus = async (paymentIntentId: string) => {
    setPolling(true);
    setPollTimeout(false);
    setShowSuccessOverlay(false);
    const maxAttempts = 60; // 2s * 60 = 120s (2 minutes)
    let attempts = 0;
    let intervalId: NodeJS.Timeout | null = null;
    let finished = false;
    const poll = async () => {
      attempts++;
      try {
        // Always fetch a fresh token — Keycloak auto-refreshes if needed.
        // Using the static prop token caused 401 loops when the token expired
        // during payment entry.
        const freshToken = await getToken();
        if (!freshToken) {
          finished = true;
          setPolling(false);
          setError('Session expired. Please refresh and try again.');
          if (intervalId) clearInterval(intervalId);
          return;
        }
        const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentId}/payment-status`, {
          headers: { 'Authorization': `Bearer ${freshToken}` },
        });
        // Stop immediately on auth failure — do not retry with the same dead token
        if (res.status === 401) {
          finished = true;
          setPolling(false);
          setError('Session expired during payment confirmation. Please refresh the page.');
          if (intervalId) clearInterval(intervalId);
          return;
        }
        const data = await res.json();
        if (data && data.success && data.data && data.data.status === 'succeeded' && data.data.paymentIntentId === paymentIntentId) {
          finished = true;
          setPolling(false);
          setSuccess(true);
          setShowSuccessOverlay(true);
          onSuccess(paymentIntentId);
          setTimeout(() => {
            setShowSuccessOverlay(false);
            onClose();
          }, 2000);
          if (intervalId) clearInterval(intervalId);
        } else if (attempts >= maxAttempts) {
          finished = true;
          setPolling(false);
          setPollTimeout(true);
          setError('Payment confirmation timed out. Please check your email or contact support.');
          if (intervalId) clearInterval(intervalId);
        }
      } catch (err) {
        setError('Error checking payment status.');
        setPolling(false);
        if (intervalId) clearInterval(intervalId);
      }
    };
    intervalId = setInterval(() => {
      if (!finished) poll();
    }, 2000);
    // Initial call immediately
    poll();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (!stripe || !elements) {
      setError("Stripe not loaded");
      setLoading(false);
      return;
    }
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        receipt_email: email || undefined,
      },
      redirect: "if_required",
    });
    if (stripeError) {
      setError(stripeError.message || "Payment failed");
      setLoading(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Start polling backend for confirmation
      pollPaymentStatus(paymentIntent.id);
    } else {
      setError("Payment not successful");
    }
    setLoading(false);
  };

  return (
    <div className="relative w-full">
      <form 
        onSubmit={handleSubmit} 
        className="w-full flex flex-col items-center gap-2" 
        aria-label="Stripe payment form" 
        autoComplete="off"
        data-lr-exclude="true"
        data-lr-sensitive="true"
        data-lr-payment-form="true"
        ref={formRef}
      >
        {/* Amount and summary */}
        <div className="w-full text-center mb-1">
          <div className="text-lg font-semibold text-[#141435]">Pay with Credit or Debit Card</div>
          {breakdown ? (
            <div className="mt-4 mb-4 bg-gray-50 p-4 rounded-lg border border-gray-100 text-left w-full text-sm">
              <div className="font-semibold text-gray-600 mb-2 uppercase tracking-wider text-xs">Fee Breakdown</div>
              <div className="flex justify-between items-center py-1">
                <span className="text-gray-700">{breakdown.baseFeeLabel || 'Initial Freeze Fee'}</span>
                <span className="text-gray-900 font-medium">${(breakdown.initialFreezeFee / 100).toFixed(2)}</span>
              </div>
              {breakdown.additionalInstruments > 0 && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-700">Additional Deeds / Instruments ({breakdown.additionalInstruments} × ${(breakdown.additionalInstrumentFee / 100).toFixed(2)})</span>
                  <span className="text-gray-900 font-medium">${((breakdown.additionalInstruments * breakdown.additionalInstrumentFee) / 100).toFixed(2)}</span>
                </div>
              )}
              {breakdown.additionalParcels > 0 && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-700">Additional Parcels ({breakdown.additionalParcels} × ${(breakdown.additionalParcelFee / 100).toFixed(2)})</span>
                  <span className="text-gray-900 font-medium">${((breakdown.additionalParcels * breakdown.additionalParcelFee) / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center">
                <span className="font-semibold text-[#141435] text-base">Total</span>
                <span className="text-2xl font-bold text-[#0B4BB6]">${(amount / 100).toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="text-2xl font-bold text-[#0B4BB6] mt-1">${(amount / 100).toFixed(2)}</div>
          )}
        </div>
        {/* Secure payment */}
        <div className="flex items-center gap-2 mb-1">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 17a2 2 0 002-2v-2a2 2 0 00-2-2 2 2 0 00-2 2v2a2 2 0 002 2z" fill="#0B4BB6"/><path d="M17 9V7a5 5 0 00-10 0v2" stroke="#0B4BB6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><rect x="5" y="9" width="14" height="10" rx="2" stroke="#0B4BB6" strokeWidth="2"/></svg>
          <span className="text-[#485D7D] text-sm">Secure payment</span>
        </div>
        {/* Receipt Email */}
        <div className="w-full text-left mb-3">
          <label htmlFor="receipt-email" className="block text-sm font-medium text-[#485D7D] mb-1">Receipt Email (Optional)</label>
          <input
            id="receipt-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0B4BB6] focus:border-[#0B4BB6]"
            data-lr-sensitive="true"
          />
        </div>
        {/* PaymentElement wrapper with custom width */}
        <div 
          style={{ width: '100%', maxWidth: 500 }}
          data-lr-exclude="true"
          data-lr-sensitive="true"
          data-lr-payment-form="true"
        >
          <PaymentElement 
            options={{ 
              layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: true,
                spacedAccordionItems: false
              },
              paymentMethodOrder: ['card', 'apple_pay', 'cashapp', 'google_pay']
            }} 
            data-lr-exclude="true"
            data-lr-sensitive="true"
          />
        </div>
        {error && <div className="text-red-500 text-sm" role="alert">{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
        {polling && <div className="text-blue-600 text-sm mt-1">Confirming payment with server...</div>}
        {pollTimeout && <div className="text-orange-600 text-sm mt-1">Payment confirmation timed out. Please check your email or contact support.</div>}
        {success && (
          <div className="flex flex-col items-center gap-1 text-green-600 text-sm mt-1">
            <svg width="32" height="32" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#22c55e"/><path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Payment successful!
          </div>
        )}
        <button
          ref={payButtonRef}
          type="submit"
          className="w-full bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#23235a] transition-colors mt-1"
          style={{ borderRadius: 60 }}
          disabled={loading || success || polling}
          aria-label={`Pay $${(amount / 100).toFixed(2)}`}
        >
          {loading ? "Processing..." : `Pay $${(amount / 100).toFixed(2)}`}
        </button>
        <button
          type="button"
          className="w-full mt-1 text-[#485D7D] underline text-sm"
          onClick={onClose}
          tabIndex={0}
          disabled={polling}
        >
          Cancel
        </button>
        {/* Powered by Stripe */}
        <div className="flex items-center justify-center mt-3">
          <img src="https://stripe.com/img/v3/home/social.png" alt="Powered by Stripe" className="h-5 mr-2" style={{ display: 'inline' }} />
          <span className="text-xs text-[#9EAABE]">Powered by Stripe</span>
        </div>
      </form>
      {/* Overlay for polling spinner */}
      <OverlaySpinner open={polling} message="Confirming your payment..." />
      {/* Overlay for success checkmark */}
      {showSuccessOverlay && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-90">
          <svg width="64" height="64" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="12" fill="#22c55e" />
            <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="text-green-600 text-xl font-semibold mt-4">Payment successful!</div>
        </div>
      )}
    </div>
  );
};

const PaymentModal: React.FC<PaymentModalProps> = ({ open, onClose, documentId, amount: initialAmount, onSuccess }) => {
  const { apiBaseUrl, document } = useUserContext();
  const { getToken } = useKeycloak();
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [serverAmount, setServerAmount] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<any>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    if (document?.paid) return; // Don't fetch payment info if already paid
    let isMounted = true;
    setLoading(true);
    setError(null);
    setClientSecret(null);
    setToken(null);

    // Step 1: fetch both publishable keys and the auth token in parallel
    Promise.all([
      fetch("/config.json").then(res => res.json()),
      getToken(),
    ])
      .then(([cfg, tok]) => {
        if (!isMounted) return;
        if (!tok) {
          setError("Not authenticated");
          setLoading(false);
          return;
        }
        setToken(tok);
        // Step 2: create the PaymentIntent — server uses live or test key
        // based on the user's access mode (liveDb vs testDb)
        return fetchClientSecret(apiBaseUrl, tok, documentId)
          .then(data => {
            if (!isMounted) return;
            if (data?.data?.status === 'already_paid') {
              if (onSuccess) onSuccess(data.data.paymentIntentId);
              onClose();
              return;
            }
            if (data?.success && data?.data?.clientSecret) {
              const secret: string = data.data.clientSecret;
              // Step 3: pick the publishable key that matches the server's key.
              const isLive = data.data.livemode === true;
              const pubKey = isLive
                ? (cfg.STRIPE_LIVE_PUBLISHABLE_KEY || '')
                : (cfg.STRIPE_TEST_PUBLISHABLE_KEY || '');
              setStripePromise(loadStripe(pubKey));
              setClientSecret(secret);
              if (data.data.amount) {
                setServerAmount(data.data.amount);
              }
              if (data.data.breakdown) {
                setBreakdown(data.data.breakdown);
              }
            } else if (data?.error) {
              setError(typeof data.error === 'string' ? data.error : data.error.message || 'Failed to get payment info');
            } else {
              setError('No client secret received from server');
            }
            setLoading(false);
          });
      })
      .catch(() => {
        if (isMounted) {
          setError("Failed to load payment info");
          setLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [open, documentId, apiBaseUrl, getToken, document?.paid]);

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  // If already paid, show payment complete UI
  if (document?.paid) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-2"
        onClick={onClose}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
      >
        <div
          ref={modalRef}
          className="bg-white rounded-2xl shadow-xl p-5 flex flex-col items-center relative min-w-[400px] max-w-[95vw] w-full outline-none"
          onClick={e => e.stopPropagation()}
          style={{ minWidth: 400, maxWidth: 400 }}
          tabIndex={0}
          data-lr-exclude="true"
          data-lr-sensitive="true"
          data-lr-payment-modal="true"
        >
          <button
            className="absolute top-4 right-4 text-[#485D7D] hover:text-[#141435] text-2xl font-bold"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <div className="flex flex-col items-center gap-2 mt-6 mb-4">
            <svg width="64" height="64" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="12" fill="#22c55e" />
              <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-green-600 text-xl font-semibold mt-4">Payment complete!</div>
          </div>
          <button
            className="w-full bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#23235a] transition-colors mt-1"
            style={{ borderRadius: 60 }}
            onClick={onClose}
            tabIndex={0}
          >
            OK
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-2"
      onClick={onClose}
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
      data-lr-exclude="true"
      data-lr-sensitive="true"
      data-lr-payment-modal="true"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-xl p-5 flex flex-col items-center relative min-w-[500px] max-w-[95vw] w-full outline-none"
        onClick={e => e.stopPropagation()}
        style={{ minWidth: 500, maxWidth: 500 }}
        tabIndex={0}
        data-lr-exclude="true"
        data-lr-sensitive="true"
        data-lr-payment-modal="true"
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-[#485D7D] hover:text-[#141435] text-2xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {/* Title */}
        {/* Payment Form or Loading/Error */}
        {loading && <div className="mb-4">Loading payment form...</div>}
        {error && <div className="mb-4 text-red-500" role="alert">{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
        {clientSecret && stripePromise && token && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <PaymentForm clientSecret={clientSecret} onSuccess={onSuccess || (() => {})} onClose={onClose} amount={serverAmount || initialAmount} breakdown={breakdown} documentId={documentId} apiBaseUrl={apiBaseUrl} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default PaymentModal; 